import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import { CircularProgress, IconButton, Typography } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import { styled } from '@mui/material/styles';
import { syncInvoicesCessionWithSII } from 'lib/api';
import { downloadAEC } from 'lib/api/reSell/factoring';
import { DocumentProfileContext } from 'lib/context/DocumentProfileProvider';
import CustomButton from 'lib/designSystem/Button';
import EnhancedTable from 'lib/designSystem/Table';
import { loggerDev, showAlert } from 'lib/functions/utils';
import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

import Editor from './components/Editor';
import EditorShoppingCart from './components/EditorShoppingCart';

export const ExpandMore = styled((props: any) => {
  const { ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }: { theme: any; expand: any }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginRight: '10px',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const DocumentsPreview = (props: any) => {
  const {
    selectedRequest,
    setSelectedRequest,
    simulationInProgress,
    onEditing,
    setSelected,
  } = props;
  const dispatch = useDispatch();
  const [miniLoading, setMiniLoading] = React.useState(false);
  const [openEditor, setOpenEditor] = React.useState(false);
  const [selectedDocuments, setSelectedDocuments] = React.useState<any>([]);
  const [openShoppingCart, setOpenShoppingCart] = React.useState(false);
  const [loadingAEC, setLoadingAEC] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);

  const { onOpenDocumentProfile } = useContext(DocumentProfileContext);

  const express = useSelector(
    (state: { auth: AuthState }) => state.auth.user?.express,
  );

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleShowBusinessDetails = (id: string) => {
    const row: any = selectedRequest?.documents?.find((x: any) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.stakeholderIdentifier,
        name: row.stakeholderName,
      }),
    );
  };
  const downloadFile = (signedUrl: string, index: number) => {
    setTimeout(() => {
      const link = document.createElement('a');
      link.href = signedUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }, index * 250);
  };

  const handleDownloadAEC = async (id: string) => {
    setLoadingAEC(true);
    try {
      const response = await downloadAEC(dispatch, [id]);
      const documentAEC = response[0];
      if (documentAEC.xml) {
        downloadFile(documentAEC.xml, documentAEC.xmlFileName);
        showAlert(dispatch, 'success', 'AEC descargado correctamente');
      } else {
        showAlert(
          dispatch,
          'error',
          'El archivo AEC no se encuentra disponible',
        );
      }
    } catch (error) {
      showAlert(dispatch, 'error', 'Error inesperado');
      console.error(error);
    }
    setLoadingAEC(false);
  };
  const auth = useSelector((state: { auth: AuthState }) => state.auth);
  const handleDownloadAECObject = {
    key: 'downloadAEC',
    label: '',
    handleAction: handleDownloadAEC,
    icon: <Typography fontWeight={800}>AEC</Typography>,
    disabled: auth.user?.express,
  };

  const canShowDownloadAEC = !auth.user?.express;

  const canShowDocumentProfile =
    selectedRequest.status === 'Aceptada' ||
    selectedRequest.status === 'Activa' ||
    selectedRequest.status === 'Completada';

  let headers: any[] = [
    ...(canShowDownloadAEC ? [handleDownloadAECObject] : []),
    ...(canShowDocumentProfile
      ? [
          {
            key: 'folio',
            label: 'Folio',
            handleAction: onOpenDocumentProfile,
            icon: <InfoIcon fontSize="small" />,
          },
        ]
      : [
          {
            key: 'folio',
            label: 'Folio',
          },
        ]),
    ...(simulationInProgress
      ? []
      : [
          {
            key: 'paidAt',
            label: 'Pagada',
            canNull: true,
            type: 'paid-lights',
          },
        ]),
    ...(selectedRequest.documents[0]?.code
      ? [{ key: 'code', label: 'Código Doc.' }]
      : []),
    { key: 'cedded', label: 'Cedida', type: 'cession-lights' },
    {
      key: 'cancelDate',
      label: 'Reclamos',
      type: 'cancel-lights',
      canNull: true,
    },
    {
      key: 'confirmDate',
      label: 'Acuse',
      type: 'confirm-lights',
      canNull: true,
    },
    {
      key: 'creditNoteAmount',
      label: 'NC',
      type: 'money-colored',
    },
    { key: 'stakeholderIdentifier', label: 'Rut pagador' },
    {
      key: 'stakeholderName',
      label: 'Pagador',
      ...(!express ? { handleAction: handleShowBusinessDetails } : {}),
      ...(!express ? { icon: <InfoIcon fontSize="small" /> } : {}),
    },
    { key: 'stakeholderSegment', label: 'Segmento pagador' },
    { key: 'issuedDate', label: 'Emisión', type: 'eight-days-date' },
    { key: 'paymentDate', label: 'Vencimiento', type: 'date' },
    { key: 'invoiceAmount', label: 'Monto original', type: 'money' },
    { key: 'amount', label: 'Monto Doc.', type: 'money' },
  ].filter(Boolean); // removes null values

  if (simulationInProgress) {
    headers = [
      ...headers,
      { key: 'costRate', label: 'Costo tasa', type: 'money' },
      { key: 'daysToCount', label: 'Días' },
      { key: 'amountRetention', label: 'Excedente', type: 'money' },
      { key: 'amountDocument', label: 'Saldo documento', type: 'money' },
      { key: 'amountToDeposit', label: 'Saldo deuda', type: 'money' },
    ];
  }

  const handleSyncWithSII = async () => {
    setMiniLoading(true);
    try {
      const response = await syncInvoicesCessionWithSII(dispatch, {
        quoteId: selectedRequest.quoteActive.id,
      });
      if (response.update) {
        addNewSyncDataToInvoices(
          response.documentsWithCessionData,
          selectedRequest.documents,
        );
        showAlert(
          dispatch,
          'success',
          'Se han actualizado los datos de los documentos con los datos del SII',
        );
      } else {
        showAlert(
          dispatch,
          'warning',
          'No hemos encontrado cambios en los documentos',
        );
      }
    } catch (error) {
      loggerDev('error', error);
    }
    setMiniLoading(false);
  };

  const addNewSyncDataToInvoices = (
    invoicesWithNewData: any,
    oldsInvoices: any,
  ) => {
    const invoicesMerged = oldsInvoices.map((oldInvoice: any) => {
      const newInvoice = invoicesWithNewData.find(
        (invoice: any) => invoice.id === oldInvoice.id,
      );
      if (newInvoice) {
        return {
          ...oldInvoice,
          cancelDate: newInvoice.cancelled,
          cedded: newInvoice.cedded,
        };
      }
    });
    setSelectedRequest({
      ...selectedRequest,
      documents: invoicesMerged,
    });
  };

  const handleEditDocuments = () => {
    setOpenEditor(true);
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ padding: 0, display: 'flex', alignItems: 'center' }}>
          <Typography fontWeight="bold" padding="0px 0px 6px" variant="h6">
            Detalle documentos
          </Typography>
          <ExpandMore
            expand={expanded}
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label="show more"
          >
            <ExpandMoreIcon />
          </ExpandMore>
        </div>
        <div>
          <div style={{ padding: '20px 0px' }}>
            {miniLoading ? (
              <CircularProgress />
            ) : (
              (selectedRequest.status === 'En revisión' ||
                selectedRequest.status === 'Borrador') && (
                <div style={{ display: 'flex', gap: 10 }}>
                  {onEditing && (
                    <CustomButton
                      color="secondary"
                      onClick={handleEditDocuments}
                      disabled={!onEditing}
                    >
                      Editar documentos y fechas
                    </CustomButton>
                  )}
                  {!onEditing && (
                    <CustomButton onClick={handleSyncWithSII} disabled>
                      Sincronizar SII
                    </CustomButton>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </div>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <EnhancedTable
          dataHeaders={headers}
          data={selectedRequest.documents}
          rowsPerPageDefault={5}
          finalSumRow
        />
      </Collapse>
      {openEditor && (
        <Editor
          open={openEditor}
          setOpen={setOpenEditor}
          selectedRequest={selectedRequest}
          subProductSelected={selectedRequest.subProduct}
          searched={false}
          setSearched={() => {}}
          businessIdentifier={selectedRequest.businessIdentifier}
          subProduct={selectedRequest.subProductName}
          selectedDocuments={selectedDocuments}
          setSelectedDocuments={setSelectedDocuments}
          setOpenShoppingCart={setOpenShoppingCart}
        />
      )}
      {openShoppingCart && (
        <EditorShoppingCart
          openShoppingCart={openShoppingCart}
          setOpenShoppingcart={setOpenShoppingCart}
          selectedDocuments={selectedDocuments}
          setSelectedDocuments={setSelectedDocuments}
          subProductName={selectedRequest.subProductName}
          setOpenDocumentsSelector={setOpenEditor}
          selectedRequest={selectedRequest}
          setSelectedRequest={setSelectedRequest}
          setSelected={setSelected}
        />
      )}
    </div>
  );
};

export default DocumentsPreview;
